/**
 * Video Call Service (Jitsi Meet)
 * 
 * Handles video call URL generation and storage for ConnectieFlow.
 * 
 * IMPORTANT: This service is ONLY for VIDEO calls.
 * Audio calls use WebRTC (see webrtcService.ts)
 * 
 * Strategy:
 * - Uses Jitsi Meet for all video calls
 * - Creates unique room per connectionFlow
 * - Both users join the SAME Jitsi room automatically
 * 
 * Benefits:
 * - Instant video calls (no auth required)
 * - Free and reliable
 * - Always works
 * - Professional quality
 * 
 * Future Options:
 * - Can self-host Jitsi for custom branding
 */

import { doc, updateDoc, getDoc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';

class MeetService {
  /**
   * Create or get a Jitsi video call URL
   * 
   * IMPORTANT: Only for VIDEO calls. Audio calls use WebRTC.
   * 
   * Strategy:
   * - Creates a unique Jitsi room per connectionFlow
   * - Both users join the SAME room
   * 
   * @param flowId - The connectionFlow document ID
   * @param stepType - Must be 'video' (call uses WebRTC)
   * @param userEmail - (Optional) User's email (not used for Jitsi)
   * @param userId - (Optional) User's ID (not used for Jitsi)
   * @returns The Jitsi video call URL
   */
  async createMeetUrlForFlow(
    flowId: string,
    stepType: 'video',
    userEmail?: string,
    userId?: string,
    displayName?: string
  ): Promise<string> {
    try {
      console.log(`[MeetService] Creating/Getting video call URL for flow ${flowId}`);

      const flowRef = doc(db, 'connectionFlows', flowId);
      
      // Step 1: Check if URL already exists
      const flowSnap = await getDoc(flowRef);
      
      if (!flowSnap.exists()) {
        throw new Error(`ConnectionFlow ${flowId} not found`);
      }
      
      const existingUrl = flowSnap.data().videoStep?.meetUrl as string | undefined;
      
      // ✅ FIX: Use sorted participant IDs for room name
      // This ensures BOTH ConnectieFlow (VideoPhase) and ChatInterface (JitsiCall)
      // generate the SAME room name, so users always end up in the same Jitsi room.
      const flowData = flowSnap.data();
      const participants = (flowData.participants || []).sort();
      const shortId = participants.length >= 2
        ? participants.map((id: string) => id.slice(0, 8)).join('-')
        : flowId.slice(0, 8); // fallback for edge cases
      const expectedRoomName = `superkonnected-${shortId}`;

      // If URL exists, is valid with ALL config flags, uses the correct room name,
      // AND does NOT contain user-specific params → reuse it.
      // Old URLs with flowId-based room names or user-specific displayName are replaced.
      if (
        existingUrl &&
        existingUrl.includes('video.superkonnected.com') &&
        existingUrl.includes('config.prejoinConfig.enabled=false') &&
        existingUrl.includes('interfaceConfig.SHOW_JITSI_WATERMARK=false') &&
        existingUrl.includes(expectedRoomName) && // ✅ Must use correct room name format
        !existingUrl.includes('userInfo.displayName') // ✅ Must NOT have user-specific displayName
      ) {
        console.log(`✅ [MeetService] REUSING existing Jitsi URL (clean, no user-specific params):`, existingUrl);
        return existingUrl;
      }
      
      if (existingUrl) {
        if (!existingUrl.includes(expectedRoomName)) {
          console.log(`🔄 [MeetService] Regenerating URL: old room name doesn't match expected "${expectedRoomName}"`);
        } else if (existingUrl.includes('userInfo.displayName')) {
          console.log(`🔄 [MeetService] Regenerating URL: removing user-specific displayName from shared URL`);
        }
      }
      
      // Step 2: Create Jitsi room
      console.log(`ℹ️ [MeetService] Creating Jitsi room: ${expectedRoomName}`);
      const roomName = expectedRoomName;
      
      // Add config parameters to skip authentication and prejoin page
      // Note: String values MUST be JSON-stringified before URL-encoding,
      // otherwise Jitsi's internal JSON.parse() will fail.
      const meetingSubject = 'SuperKonnected videogesprek';
      const jitsiConfig = [
        // ✅ Skip prejoin page / naam-invulscherm
        'config.prejoinConfig.enabled=false',
        'config.prejoinPageEnabled=false',
        'config.requireDisplayName=false',
        // ✅ Direct met audio + video aan starten
        'config.startWithAudioMuted=false',
        'config.startWithVideoMuted=false',
        // ✅ Mooie titel in plaats van technische room-ID
        `config.subject=${encodeURIComponent(JSON.stringify(meetingSubject))}`,
        // ✅ Extra config
        'config.disableDeepLinking=true',
        'config.enableWelcomePage=false',
        'config.enableClosePage=false',
        'config.enableInsecureRoomNameWarning=false',
        'config.enableLobbyChat=false',
        'config.hideLobbyButton=true',
        // ✅ Verberg ALLE Jitsi branding/logo's
        'interfaceConfig.SHOW_JITSI_WATERMARK=false',
        'interfaceConfig.SHOW_WATERMARK_FOR_GUESTS=false',
        'interfaceConfig.SHOW_BRAND_WATERMARK=false',
        'interfaceConfig.SHOW_POWERED_BY=false',
        'interfaceConfig.SHOW_PROMOTIONAL_CLOSE_PAGE=false',
        'interfaceConfig.SHOW_CHROME_EXTENSION_BANNER=false',
        'interfaceConfig.HIDE_DEEP_LINKING_LOGO=true',
        'interfaceConfig.HIDE_INVITE_MORE_HEADER=true',
        'interfaceConfig.MOBILE_APP_PROMO=false',
        'interfaceConfig.DISPLAY_WELCOME_PAGE_CONTENT=false',
        'interfaceConfig.DISPLAY_WELCOME_FOOTER=false',
        `interfaceConfig.APP_NAME=${encodeURIComponent(JSON.stringify('SuperKonnected'))}`,
        `interfaceConfig.NATIVE_APP_NAME=${encodeURIComponent(JSON.stringify('SuperKonnected'))}`,
        `interfaceConfig.PROVIDER_NAME=${encodeURIComponent(JSON.stringify('SuperKonnected'))}`,
        `interfaceConfig.DEFAULT_BACKGROUND=${encodeURIComponent(JSON.stringify('#0A2342'))}`,
      ].join('&');
      
      // ✅ FIX: Do NOT include userInfo.displayName in the SHARED URL
      // Each user's VideoPhase/JitsiCall component adds their OWN display name
      // This prevents any URL mismatch between users
      const newVideoUrl = `https://video.superkonnected.com/${roomName}#${jitsiConfig}`;
      const provider = 'jitsi';
      console.log(`✅ [MeetService] Created Jitsi room:`, newVideoUrl);
      
      // Step 5: Save to Firestore (use transaction to prevent race conditions)
      await runTransaction(db, async (transaction) => {
        const freshSnap = await transaction.get(flowRef);
        
        // Check again if someone else created a URL in the meantime with correct room name + no user-specific params
        const existingCheck = freshSnap.data()?.videoStep?.meetUrl as string | undefined;
        if (
          existingCheck &&
          existingCheck.includes('video.superkonnected.com') &&
          existingCheck.includes('config.prejoinConfig.enabled=false') &&
          existingCheck.includes('interfaceConfig.SHOW_JITSI_WATERMARK=false') &&
          existingCheck.includes(roomName) && // ✅ Must match our expected room name
          !existingCheck.includes('userInfo.displayName') // ✅ Must NOT have user-specific params
        ) {
          console.log(`ℹ️ [MeetService] Another user created clean Jitsi URL in the meantime (room: ${roomName}), using that one`);
          return; // Don't overwrite
        }
        
        transaction.update(flowRef, {
          'videoStep.meetUrl': newVideoUrl,
          'videoStep.provider': provider,
          'updatedAt': serverTimestamp()
        });
      });
      
      console.log(`✅ [MeetService] Video call URL saved: ${provider} - ${newVideoUrl}`);
      
      return newVideoUrl;
      
    } catch (error) {
      console.error(`❌ [MeetService] Error creating video call URL:`, error);
      throw error;
    }
  }
  
  /**
   * Get existing Jitsi video call URL from video step
   * 
   * @param flowId - The connectionFlow document ID
   * @returns The Jitsi video call URL or null if not found
   */
  async getMeetUrlFromFlow(
    flowId: string
  ): Promise<string | null> {
    try {
      const flowRef = doc(db, 'connectionFlows', flowId);
      const flowSnap = await getDoc(flowRef);
      
      if (!flowSnap.exists()) {
        console.warn(`❌ [MeetService] Flow ${flowId} not found`);
        return null;
      }
      
      const flowData = flowSnap.data();
      
      // Get from videoStep
      const meetUrl = flowData.videoStep?.meetUrl || null;
      
      if (meetUrl) {
        console.log(`✅ [MeetService] Retrieved Jitsi URL:`, meetUrl);
      } else {
        console.warn(`⚠️ [MeetService] No Jitsi URL found for flow ${flowId}`);
      }
      
      return meetUrl;
    } catch (error) {
      console.error(`❌ [MeetService] Error getting video call URL:`, error);
      return null;
    }
  }
  
  /**
   * Manually set a Jitsi URL for video step (for admin/testing purposes)
   * 
   * @param flowId - The connectionFlow document ID
   * @param meetUrl - The Jitsi URL to set
   */
  async setMeetUrlForFlow(
    flowId: string,
    meetUrl: string
  ): Promise<void> {
    try {
      const flowRef = doc(db, 'connectionFlows', flowId);
      
      await updateDoc(flowRef, {
        'videoStep.meetUrl': meetUrl,
        googleMeetUrl: meetUrl,  // Legacy/fallback
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ [MeetService] Set Meet URL for video:`, meetUrl);
    } catch (error) {
      console.error(`❌ [MeetService] Error setting Meet URL:`, error);
      throw error;
    }
  }

  /**
   * Check if a Jitsi URL exists for video step
   */
  async hasMeetUrl(flowId: string): Promise<boolean> {
    const meetUrl = await this.getMeetUrlFromFlow(flowId);
    return meetUrl !== null && meetUrl.length > 0;
  }

  /**
   * Open Jitsi video call in a COMPACT POPUP WINDOW (1/4 size)
   * 
   * Creates a SMALL popup window (400x500px) positioned at the TOP RIGHT.
   * This is 1/4 of normal size, leaving plenty of space for timer and notes.
   * 
   * @param meetUrl - The Jitsi video call URL
   * @param userEmail - (Optional) User's email (not used for Jitsi)
   * @returns The window object (or null if blocked)
   */
  openMeetWindow(meetUrl: string, userEmail?: string): Window | null {
    try {
      console.log(`📹 [MeetService] Opening Jitsi in COMPACT popup (1/4 size)`);

      // COMPACT window - 1/4 of normal size for side-by-side viewing
      const width = 400;   // Compact width (was 500)
      const height = 500;  // Compact height (was 700)
      
      // Position at TOP RIGHT corner of the screen
      const left = window.screen.width - width - 20;  // 20px margin from right
      const top = 80; // 80px from top (gives some space from browser chrome)

      // Window features for a compact popup
      const features = [
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
        'toolbar=no',        // No browser toolbar
        'location=no',       // No URL bar (cleaner look)
        'status=no',         // No status bar
        'menubar=no',        // No menu bar
        'scrollbars=no',     // No scrollbars needed
        'resizable=yes',     // User can resize if needed
        'noopener',          // Security
        'noreferrer'         // Security
      ].join(',');

      console.log(`📹 [MeetService] Opening video call popup: ${width}x${height} at top-right`);

      // Open popup window (use unique window name to prevent conflicts)
      const windowName = 'SuperKonnectedVideoCall';
      const popup = window.open(meetUrl, windowName, features);

      if (!popup) {
        console.warn('⚠️ [MeetService] Popup blocked by browser');
        alert('Popup geblokkeerd!\n\nSta popups toe voor deze site om Jitsi te gebruiken.\n\nKlik op het icoon in de adresbalk en sta popups toe.');
        return null;
      }

      // ✅ Focus popup briefly to ensure it's visible, then let user interact naturally
      popup.focus();
      
      // ⚠️ DON'T call window.focus() - it pushes popup behind main window!
      // Instead, let the user click naturally in main window (which will focus textarea via onClick handler)
      
      console.log('ℹ️ [MeetService] Popup opened. Use Ctrl+N to type notes without losing Jitsi!');

      // 💡 Show one-time helper for window positioning
      this.showWindowPositioningHelper();
      
      // Also show a quick reminder in console
      console.log('💡 TIP: Druk Ctrl+N (of Cmd+N) om notities te maken zonder Jitsi te verliezen!');

      console.log('✅ [MeetService] Compact Jitsi popup opened successfully (1/4 size)');
      return popup;

    } catch (error) {
      console.error('❌ [MeetService] Error opening Jitsi window:', error);
      // Fallback to new tab if popup fails
      window.open(meetUrl, '_blank', 'noopener,noreferrer');
      return null;
    }
  }
  
  /**
   * Close the video call window if it exists (note: limited control over external window)
   */
  closeMeetWindow(): void {
    // Note: We have limited control over popup windows due to browser security.
    // Users can close the popup manually, or it closes when they end the video call.
    console.log('ℹ️ [MeetService] Video call popup should be closed manually by user');
  }

  /**
   * Show one-time helper banner with window positioning instructions
   */
  private showWindowPositioningHelper(): void {
    // Check if user has already seen this helper (localStorage)
    const hasSeenHelper = localStorage.getItem('meet_window_helper_seen');
    
    if (hasSeenHelper === 'true') {
      return; // Don't show again
    }

    // Create helper banner
    const helper = document.createElement('div');
    helper.id = 'meet-window-helper';
    helper.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.3);
      z-index: 10000;
      max-width: 600px;
      font-family: system-ui, -apple-system, sans-serif;
      animation: slideDown 0.3s ease-out;
    `;

    helper.innerHTML = `
      <div style="display: flex; align-items: start; gap: 16px;">
        <div style="font-size: 32px; flex-shrink: 0;">💡</div>
        <div style="flex: 1;">
          <div style="font-weight: 600; font-size: 16px; margin-bottom: 8px;">
            💡 Tip: Notities maken zonder Jitsi te verliezen
          </div>
          <div style="font-size: 14px; line-height: 1.5; margin-bottom: 12px; opacity: 0.95;">
            <strong>✨ Beste optie:</strong> Druk <kbd style="background: rgba(255,255,255,0.2); padding: 2px 6px; border-radius: 4px; font-family: monospace; font-weight: bold;">Ctrl + N</kbd> (of <kbd style="background: rgba(255,255,255,0.2); padding: 2px 6px; border-radius: 4px; font-family: monospace; font-weight: bold;">Cmd + N</kbd> op Mac) om notities te typen<br>
            <strong>Alternatief:</strong> <kbd style="background: rgba(255,255,255,0.2); padding: 2px 6px; border-radius: 4px; font-family: monospace;">Win + ←</kbd> voor split screen<br>
            <strong>Of:</strong> <kbd style="background: rgba(255,255,255,0.2); padding: 2px 6px; border-radius: 4px; font-family: monospace;">Alt + Tab</kbd> om tussen vensters te wisselen
          </div>
          <div style="display: flex; gap: 12px;">
            <button id="helper-ok" style="
              background: rgba(255,255,255,0.2);
              border: 1px solid rgba(255,255,255,0.3);
              color: white;
              padding: 6px 16px;
              border-radius: 6px;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              transition: background 0.2s;
            " onmouseover="this.style.background='rgba(255,255,255,0.3)'" onmouseout="this.style.background='rgba(255,255,255,0.2)'">
              Begrepen ✓
            </button>
            <button id="helper-dont-show" style="
              background: transparent;
              border: none;
              color: rgba(255,255,255,0.8);
              padding: 6px 16px;
              font-size: 13px;
              cursor: pointer;
              text-decoration: underline;
            ">
              Niet meer tonen
            </button>
          </div>
        </div>
      </div>
    `;

    // Add animation keyframes
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateX(-50%) translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(helper);

    // Handle "OK" button
    const okButton = document.getElementById('helper-ok');
    okButton?.addEventListener('click', () => {
      helper.style.animation = 'slideDown 0.3s ease-out reverse';
      setTimeout(() => helper.remove(), 300);
    });

    // Handle "Don't show again" button
    const dontShowButton = document.getElementById('helper-dont-show');
    dontShowButton?.addEventListener('click', () => {
      localStorage.setItem('meet_window_helper_seen', 'true');
      helper.style.animation = 'slideDown 0.3s ease-out reverse';
      setTimeout(() => helper.remove(), 300);
    });

    // Auto-hide after 15 seconds
    setTimeout(() => {
      if (document.body.contains(helper)) {
        helper.style.animation = 'slideDown 0.3s ease-out reverse';
        setTimeout(() => helper.remove(), 300);
      }
    }, 15000);

    console.log('💡 [MeetService] Window positioning helper shown');
  }
}

// Export singleton instance
export const meetService = new MeetService();






